import {Component, computed, Input, OnInit, Signal} from '@angular/core';
import {ActivatedRoute, RouterModule} from '@angular/router';
import {BehaviorSubject, combineLatest, Observable, of, switchMap} from 'rxjs';
import {catchError, filter, map, tap} from 'rxjs/operators';
import {errorText} from '../../../graphql/error-link';
import {Apollo} from 'apollo-angular';
import {Airline, PoItem, Query} from '../../../types-workflow';
import gql from 'graphql-tag';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {BreadCrumb, LogoAreaComponent} from '../../logo-area/logo-area.component';
import {Organization} from '../../../types-repository';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';
import {FilterSectionComponent} from '../../filter-section/filter-section.component';
import { AsyncPipe, DatePipe } from '@angular/common';
import {ContextService} from '../../../services/context.service';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {DateComponent} from '../../../date/date.component';

const query = gql`
  query GetPreviews($airline: String!, $date_created: AWSDateTime) {
    list_POs(input: {
      filter: {
        airline: { eq: $airline}
        date_created: {gt: $date_created}
      } }){
      pos {
        id
        airline
        po_name
        date_created
        poitems {
          filename
          id
          po_item_id
          preview {
            dash_manifest_last_modified
            dash_manifest_url
            hls_manifest_url
            hls_manifest_last_modified
          }
        }
      }
    }
  }
`

@Component({
  selector: 'app-preview',
  templateUrl: './list-previews.component.html',
  styleUrls: ['./list-previews.component.scss'],
  imports: [
    LogoAreaComponent,
    HeadlineSectionComponent,
    FilterSectionComponent,
    ReactiveFormsModule,
    RouterModule,
    DateComponent
]
})
export class ListPreviewsForAirlineComponent implements OnInit {

  title = new BehaviorSubject<string>(null);
  po$: Signal<string> = null;
  poItems$: Signal<PoItem[]> = null;
  poItemsSortedFiltered$: Signal<PoItem[]> = null;
  loading = true;
  /** Why the previews could not be loaded. */
  loadError: string | null = null;

  filters: FormGroup = new FormGroup({
    t: new FormControl<string>(''),
  });
  breadCrumbs: Signal<BreadCrumb[]>;
  protected airline: Signal<Airline | null>;
  organization: Signal<Organization | null>;

  ngOnInit() {
  }

  constructor(
    private route: ActivatedRoute,
    contextService: ContextService,
    private apollo: Apollo) {

    this.airline = contextService.airline;
    this.organization = contextService.organization;

    this.po$ = toSignal(this.route.paramMap.pipe(
      map(paramMap => paramMap.get('po')),
    ))

    this.breadCrumbs = computed(() =>
      [
        {display: 'Previews', commands: undefined }
      ]);

    const sevenWeeksAgo = new Date();
    sevenWeeksAgo.setDate(sevenWeeksAgo.getDate() - 7 * 7);
    this.poItems$ = toSignal(combineLatest(toObservable(this.airline), toObservable(this.organization)).pipe(
      tap(([airline, organization]) => console.log(airline?.iata_code, organization?.name)),
      switchMap(([airline, organization]) => {
          if (!airline || !organization) {
            this.loading = false
            return of([]); // Emit an empty array if either is missing
          }
          this.loadError = null;
          return this.apollo.use('workflow').query<Query>({
            query: query,
            // 'all', not the default 'none': one preview resolver failing (e.g. an item deleted between
            // listing and lookup) nulls only that item's preview, which the filter below drops. Only a
            // response without the PO list counts as a failed load.
            errorPolicy: 'all',
            variables: {
              airline: airline.iata_code,
              date_created: sevenWeeksAgo.toISOString()
            }
          }).pipe(
            map(res => {
              if (!res.data?.list_POs) {
                throw res.error ?? new Error('No previews in the response');
              }
              return res.data.list_POs.pos
            }),
            map(pos => {
              pos = pos.map(po => Object.assign({}, po))
              for (const po of pos) {
                // An item's PO is the enclosing one: taken from here rather than from POItem.po, a resolver of
                // its own that can fail independently under errorPolicy 'all' and leave po null (Percival).
                po.poitems = po.poitems.map(poitem => Object.assign({}, poitem, {po: {po_name: po.po_name, airline: po.airline}}))
                po.poitems = po.poitems.filter(poitem =>
                  poitem.preview !== null && (poitem.preview.hls_manifest_url !== null || poitem.preview.dash_manifest_url !== null))
              }
              pos = pos.filter(po => po.poitems.length > 0)
              return pos
            }),
            map(pos => {
              return pos.map(po => po.poitems).reduce((a, b) => [].concat(a, b), [])
            }),
            tap(poitems => this.loading = false),
            // A failed load says why instead of loading forever; the next airline loads again.
            catchError(error => {
              this.loading = false;
              this.loadError = errorText(error);
              return of([]);
            }),
          )
        }
      )
    ))
    const filterChangedSignal = toSignal(this.filters.valueChanges)
    this.poItemsSortedFiltered$ = computed(() => {
      const poItems = this.poItems$()
      const filters = filterChangedSignal()
      if (!poItems || poItems.length === 0) {
        return [];
      }
      if (!filters) {
        return poItems
      }
      return poItems.filter(poitem => {
        return poitem.po_item_id.toLowerCase().includes((filters['t'] || '').toLowerCase()) ||
          poitem.po.po_name.toLowerCase().includes((filters['t'] || '').toLowerCase());
      })
    })

  }
}
